import { useEmergencyStore } from '@/stores/emergency-store';
import type { Patrol } from '@/stores/emergency-store';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Motorbike,
  MapPin,
  Phone,
  Clock,
  Circle,
  Navigation,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

function getPatrolStatusIcon(status: Patrol['status']) {
  switch (status) {
    case 'AVAILABLE':
      return <Circle className="w-3 h-3 fill-green-400 text-green-400" />;
    case 'BACKGROUND':
      return <Circle className="w-3 h-3 fill-cyan-400 text-cyan-400" />;
    case 'ASSIGNED':
      return <AlertCircle className="w-3 h-3 text-amber-400" />;
    case 'EN_ROUTE':
      return <Navigation className="w-3 h-3 text-blue-400" />;
    case 'ON_SCENE':
      return <CheckCircle className="w-3 h-3 text-green-400" />;
    default:
      return <Circle className="w-3 h-3 text-slate-500" />;
  }
}

function getPatrolStatusName(status: Patrol['status']) {
  const names: Record<Patrol['status'], string> = {
    AVAILABLE: 'Disponible',
    BACKGROUND: 'Segundo plano',
    ASSIGNED: 'Asignado',
    EN_ROUTE: 'En camino',
    ON_SCENE: 'En sitio',
    OFFLINE: 'Sin conexión',
  };
  return names[status];
}

function getPatrolStatusColor(status: Patrol['status']) {
  switch (status) {
    case 'AVAILABLE':
      return 'bg-green-500/20 border-green-500/30';
    case 'BACKGROUND':
      return 'bg-cyan-500/20 border-cyan-500/30';
    case 'ASSIGNED':
      return 'bg-amber-500/20 border-amber-500/30';
    case 'EN_ROUTE':
      return 'bg-blue-500/20 border-blue-500/30';
    case 'ON_SCENE':
      return 'bg-emerald-500/20 border-emerald-500/30';
    default:
      return 'bg-slate-500/20 border-slate-500/30';
  }
}

export function PatrolList() {
  const { patrols, patrolsLoading, selectPatrol } = useEmergencyStore();

  // Group by status. OFFLINE patrols MUST stay visible — they were
  // disappearing entirely before because the layout had no group for them,
  // so the operator could not even see/call a patrol whose phone was off.
  const availablePatrols = patrols.filter((p) => p.status === 'AVAILABLE' || p.status === 'BACKGROUND');
  const busyPatrols = patrols.filter((p) => ['ASSIGNED', 'EN_ROUTE', 'ON_SCENE'].includes(p.status));
  const offlinePatrols = patrols.filter((p) => p.status === 'OFFLINE');

  if (patrolsLoading && patrols.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400">
          <div className="w-5 h-5 border-2 border-gray-400 dark:border-slate-400 border-t-transparent rounded-full animate-spin" />
          <span>Cargando patrulleros...</span>
        </div>
      </div>
    );
  }

  if (patrols.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <div className="w-16 h-16 bg-gray-200 dark:bg-slate-700 rounded-full flex items-center justify-center mb-4">
          <Motorbike className="w-8 h-8 text-gray-500 dark:text-slate-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Sin patrulleros activos</h3>
        <p className="text-gray-500 dark:text-slate-400 text-sm">
          Los patrulleros aparecerán aquí cuando estén en línea
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      {/* Stats */}
      <div className="p-4 border-b border-gray-200 dark:border-slate-800">
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <Circle className="w-3 h-3 fill-green-400 text-green-400" />
            <span className="text-gray-700 dark:text-slate-300 text-sm">
              {availablePatrols.length} disponible{availablePatrols.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <AlertCircle className="w-3 h-3 text-amber-400" />
            <span className="text-gray-700 dark:text-slate-300 text-sm">
              {busyPatrols.length} ocupado{busyPatrols.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Available patrols */}
      {availablePatrols.length > 0 && (
        <div>
          <div className="px-4 py-2 bg-gray-100 dark:bg-slate-800/50">
            <h4 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase">Disponibles</h4>
          </div>
          {availablePatrols.map((patrol) => (
            <PatrolCard key={patrol.id} patrol={patrol} onSelect={selectPatrol} />
          ))}
        </div>
      )}

      {/* Busy patrols */}
      {busyPatrols.length > 0 && (
        <div>
          <div className="px-4 py-2 bg-gray-100 dark:bg-slate-800/50">
            <h4 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase">En servicio</h4>
          </div>
          {busyPatrols.map((patrol) => (
            <PatrolCard key={patrol.id} patrol={patrol} onSelect={selectPatrol} />
          ))}
        </div>
      )}

      {/* Offline patrols */}
      {offlinePatrols.length > 0 && (
        <div>
          <div className="px-4 py-2 bg-gray-100 dark:bg-slate-800/50">
            <h4 className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase">
              Sin conexión ({offlinePatrols.length})
            </h4>
          </div>
          {offlinePatrols.map((patrol) => (
            <PatrolCard key={patrol.id} patrol={patrol} onSelect={selectPatrol} />
          ))}
        </div>
      )}
    </div>
  );
}

interface PatrolCardProps {
  patrol: Patrol;
  onSelect: (patrol: Patrol) => void;
}

function PatrolCard({ patrol, onSelect }: PatrolCardProps) {
  return (
    <button
      onClick={() => onSelect(patrol)}
      className={`w-full p-4 border-b border-gray-200 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors text-left`}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center border ${getPatrolStatusColor(
            patrol.status
          )}`}
        >
          <Motorbike className="w-5 h-5 text-gray-600 dark:text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900 dark:text-white">{patrol.name}</span>
            <div className="flex items-center gap-1">
              {getPatrolStatusIcon(patrol.status)}
              <span className="text-xs text-gray-500 dark:text-slate-400">{getPatrolStatusName(patrol.status)}</span>
            </div>
          </div>



          {/* Phone */}
          {patrol.phone && (
            <a
              href={`tel:${patrol.phone}`}
              onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-1 text-blue-400 text-xs hover:underline mb-1"
            >
              <Phone className="w-3 h-3" />
              {patrol.phone}
            </a>
          )}

          {/* Last update */}
          <div className="flex items-center gap-1 text-gray-500 dark:text-slate-500 text-xs">
            <Clock className="w-3 h-3" />
            <span>
              Actualizado {formatDistanceToNow(new Date(patrol.last_update), {
                addSuffix: true,
                locale: es,
              })}
            </span>
          </div>
        </div>

        {/* Location link */}
        <a
          href={`https://www.google.com/maps?q=${patrol.latitude},${patrol.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="p-2 text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
        >
          <MapPin className="w-4 h-4" />
        </a>
      </div>
    </button>
  );
}
